import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { AlertCircle, ArrowLeft, DollarSign, Package } from 'lucide-react';

const MissingReturnedAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' ? { filter, ...customDates } : { filter };
      const response = await analyticsService.getMissingReturnedAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching missing/returned analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800">{value}</h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout title="Missing & Returned Shipments" icon={AlertCircle}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Missing & Returned Shipments"
      icon={AlertCircle}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Track and analyze shipment issues</p>

                {/* Summary Cards */}
                {data && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Total Missing"
                        value={data.summary.total_missing}
                        subtitle="Missing shipments"
                        icon={AlertCircle}
                        color="bg-red-500"
                      />
                      <StatCard
                        title="Total Returned"
                        value={data.summary.total_returned}
                        subtitle="Returned shipments"
                        icon={ArrowLeft}
                        color="bg-blue-600"
                      />
                      <StatCard
                        title="Total Affected"
                        value={data.summary.total_affected}
                        subtitle="Combined count"
                        icon={Package}
                        color="bg-yellow-500"
                      />
                      <StatCard
                        title="Financial Impact"
                        value={formatCurrency(data.summary.total_financial_impact)}
                        subtitle="Total value"
                        icon={DollarSign}
                        color="bg-purple-500"
                      />
                    </div>

                    {/* Missing Shipments Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Missing Shipments */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Missing Shipments</h2>
                        </div>

                        <div className="space-y-4">
                          {/* By Status */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Approval Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.missing_shipments.by_approval_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-red-50 rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-red-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Financial Impact */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Financial Impact</h3>
                            <div className="bg-gray-50 p-3 rounded">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-gray-600">Count:</span>
                                <span className="text-sm font-semibold">{data.missing_shipments.financial_impact.count}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Value:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.missing_shipments.financial_impact.total_value)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Top Reasons */}
                          {data.missing_shipments.by_reason && data.missing_shipments.by_reason.length > 0 && (
                            <div className="border-t border-gray-200 pt-4">
                              <h3 className="text-sm font-medium text-gray-700 mb-2">Top Reasons</h3>
                              <div className="space-y-1">
                                {data.missing_shipments.by_reason.slice(0, 5).map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-600 truncate mr-2">{item.reason}</span>
                                    <span className="font-semibold text-gray-800">{item.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Returned Shipments */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <ArrowLeft className="w-6 h-6 text-blue-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Returned Shipments</h2>
                        </div>

                        <div className="space-y-4">
                          {/* By Return Status */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Return Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.returned_shipments.by_return_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-blue-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* By Approval Status */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Approval Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.returned_shipments.by_approval_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-brand-light rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-brand">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Financial Impact */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Financial Impact</h3>
                            <div className="bg-gray-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Count:</span>
                                <span className="text-sm font-semibold">{data.returned_shipments.financial_impact.count}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Value:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(data.returned_shipments.financial_impact.total_value)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Refund Amount:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.returned_shipments.financial_impact.refund_amount)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Payment Workflow */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Workflow</h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                                <span className="text-sm text-gray-700">Payment Initiated</span>
                                <span className="text-sm font-semibold text-yellow-600">
                                  {data.returned_shipments.payment_workflow.payment_initiated}
                                </span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                                <span className="text-sm text-gray-700">Payment Refunded</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {data.returned_shipments.payment_workflow.payment_refunded}
                                </span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                                <span className="text-sm text-gray-700">Pending Refund</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {data.returned_shipments.payment_workflow.pending_refund}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Return Reasons */}
                    {data.returned_shipments.top_reasons && data.returned_shipments.top_reasons.length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Top Return Reasons</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          {data.returned_shipments.top_reasons.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-card">
                              <span className="text-sm text-gray-700 flex-1 mr-3">{item.reason}</span>
                              <span className="text-sm font-semibold text-gray-800 bg-white px-2 py-1 rounded">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
    </PageLayout>
  );
};

export default MissingReturnedAnalytics;